import { create } from "zustand";
import { persist } from "zustand/middleware";
import { authApi } from "../lib/api";
import { setUser } from "../lib/sentry";

interface User {
  id: string;
  email: string;
}

interface Session {
  access_token: string;
  refresh_token?: string;
  expires_at?: number;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isInitialized: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    confirmPassword: string
  ) => Promise<{ requiresConfirmation: boolean }>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  setLoading: (loading: boolean) => void;
}

// Module-level promise to prevent concurrent refresh attempts
let refreshPromise: Promise<boolean> | null = null;

// Buffer time before expiration to trigger refresh (5 minutes)
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      isLoading: false,
      isInitialized: false,

      setLoading: (loading) => set({ isLoading: loading }),

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const data = await authApi.login(email, password);
          set({
            user: data.user,
            session: data.session,
            isLoading: false,
          });
          // Set Sentry user context for error tracking
          setUser({ id: data.user.id, email: data.user.email });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (email, password, confirmPassword) => {
        set({ isLoading: true });
        try {
          const data = await authApi.register(email, password, confirmPassword);

          if (data.requiresConfirmation) {
            // Don't set user/session - they need to confirm email first
            set({ isLoading: false });
            return { requiresConfirmation: true };
          }

          // Immediate login (email confirmation disabled in Supabase)
          set({
            user: data.user,
            session: data.session,
            isLoading: false,
          });
          // Set Sentry user context for error tracking
          setUser({ id: data.user.id, email: data.user.email });
          return { requiresConfirmation: false };
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        const { session } = get();
        set({ isLoading: true });
        try {
          if (session?.access_token) {
            await authApi.logout(session.access_token).catch(() => {
              // Ignore logout errors, clear state anyway
            });
          }
        } finally {
          set({
            user: null,
            session: null,
            isLoading: false,
          });
          // Clear Sentry user context
          setUser(null);
        }
      },

      refreshSession: async () => {
        const { session } = get();

        if (!session?.refresh_token) {
          // No refresh token, logout
          await get().logout();
          return false;
        }

        // Return existing promise if refresh is already in-flight
        if (refreshPromise) {
          return refreshPromise;
        }

        refreshPromise = (async () => {
          try {
            const data = await authApi.refresh(session.refresh_token!);
            set({
              user: data.user,
              session: data.session,
            });
            // Update Sentry user context with refreshed user data
            setUser({ id: data.user.id, email: data.user.email });
            return true;
          } catch {
            // Refresh failed, logout
            await get().logout();
            return false;
          } finally {
            refreshPromise = null;
          }
        })();

        return refreshPromise;
      },

      checkSession: async () => {
        const { session, refreshSession } = get();
        if (!session?.access_token) {
          set({ isInitialized: true });
          return;
        }

        // Check if token is about to expire (within buffer time)
        if (session.expires_at && session.refresh_token) {
          const expiresAtMs = session.expires_at * 1000;
          const now = Date.now();

          if (expiresAtMs - now < REFRESH_BUFFER_MS) {
            // Token is about to expire, refresh first
            const refreshed = await refreshSession();
            if (!refreshed) {
              set({ isInitialized: true });
              return;
            }
            // After refresh, continue with session check using new token
            const newSession = get().session;
            if (!newSession?.access_token) {
              set({ isInitialized: true });
              return;
            }
          }
        }

        set({ isLoading: true });
        try {
          const currentSession = get().session;
          const data = await authApi.session(currentSession!.access_token);
          if (data.user && data.session) {
            set({
              user: data.user,
              session: { ...currentSession, ...data.session },
              isLoading: false,
              isInitialized: true,
            });
            // Set Sentry user context for restored session
            setUser({ id: data.user.id, email: data.user.email });
          } else {
            // Session invalid, clear state
            set({
              user: null,
              session: null,
              isLoading: false,
              isInitialized: true,
            });
            // Clear Sentry user context
            setUser(null);
          }
        } catch {
          // Error checking session, clear state
          set({
            user: null,
            session: null,
            isLoading: false,
            isInitialized: true,
          });
          // Clear Sentry user context
          setUser(null);
        }
      },
    }),
    {
      name: "runlens-auth",
      partialize: (state) => ({
        session: state.session,
      }),
    }
  )
);
